import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import Home from "./pages/Home";
import LandingLang from "./pages/LandingLang";
import Auth from "./pages/Auth";
import AuthRedirect from "./pages/AuthRedirect";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import LeaguePage from "./pages/LeaguePage";
import Invite from "./pages/Invite";
import Profile from "./pages/Profile";
import GlobalMatches from "./pages/GlobalMatches";
import { LeagueRedirect } from "./pages/LeagueRedirect";
import AdminPage from "./pages/Admin";
import { RequireSuperAdmin } from "./components/RequireSuperAdmin";
import NotFound from "./pages/NotFound";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Cookies from "./pages/Cookies";
import Unsubscribe from "./pages/Unsubscribe";
import BulkTips from "./pages/BulkTips";
import Help from "./pages/Help";
import SentryTest from "./pages/SentryTest";
import Credits from "./pages/Credits";
import About from "./pages/About";
import Contact from "./pages/Contact";
import HowItWorks from "./pages/HowItWorks";
import ScoringRules from "./pages/ScoringRules";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import { AdConsentBanner } from "./components/ads/AdConsentBanner";



const queryClient = new QueryClient();

const SyncLang = () => {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  useEffect(() => {
    if (!user) return;
    const stored = localStorage.getItem("lang");
    if (stored) return; // respect user's explicit language choice
    supabase.from("profiles").select("language").eq("id", user.id).maybeSingle().then(({ data }) => {
      if (data?.language && data.language !== i18n.resolvedLanguage) i18n.changeLanguage(data.language);
    });
  }, [user, i18n]);
  return null;
};

const LegacyLeagueRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/league/${id}`} replace />;
};

const LegacyInviteRedirect = () => {
  const { code } = useParams();
  return <Navigate to={`/invite/${code}`} replace />;
};



const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <SyncLang />
          <Routes>
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/redirect" element={<AuthRedirect />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/invite/:code" element={<Invite />} />
            <Route path="/" element={<Home />} />
            <Route path="/cs" element={<LandingLang />} />
            <Route path="/en" element={<LandingLang />} />
            <Route path="/matches" element={<ProtectedRoute><GlobalMatches /></ProtectedRoute>} />
            <Route path="/predict" element={<ProtectedRoute><BulkTips /></ProtectedRoute>} />
            <Route path="/tips" element={<ProtectedRoute><LeagueRedirect tab="matches" /></ProtectedRoute>} />
            <Route path="/standings" element={<ProtectedRoute><LeagueRedirect tab="table" /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/league/:id" element={<ProtectedRoute><LeaguePage /></ProtectedRoute>} />
            <Route path="/admin" element={<RequireSuperAdmin><AdminPage /></RequireSuperAdmin>} />
            {/* Legacy Czech routes — redirect to new English paths */}
            <Route path="/pozvanka/:code" element={<LegacyInviteRedirect />} />
            <Route path="/profil" element={<Navigate to="/profile" replace />} />
            <Route path="/liga/:id" element={<LegacyLeagueRedirect />} />
            <Route path="/zapasy" element={<Navigate to="/matches" replace />} />
            <Route path="/tipy" element={<Navigate to="/tips" replace />} />
            <Route path="/tabulka" element={<Navigate to="/standings" replace />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/help" element={<Help />} />
            <Route path="/admin/sentry-test" element={<RequireSuperAdmin><SentryTest /></RequireSuperAdmin>} />
            <Route path="/credits" element={<Credits />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/scoring-rules" element={<ScoringRules />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="*" element={<NotFound />} />

          </Routes>
          <AdConsentBanner />
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
